/**
 * SDX React error boundary factory.
 *
 * Every mounted component is wrapped in an error boundary so a
 * runtime exception in one component does not tear down the
 * entire SDX mount tree. The boundary renders a minimal inline
 * fallback (title + error message) and dispatches an
 * `sdx:component-error` window event so sdx_devel can surface
 * crash details in its toolbar.
 *
 * The factory memoizes the class per-React-instance: once React
 * is loaded we build a single ErrorBoundary class and reuse it
 * for every component mount. Building it fresh per mount would
 * defeat React's reconciler (each render would see a new class
 * identity) and cause pointless re-mounts on props change.
 */

import type { ReactLike } from './types';

type BoundaryInstance = {
  state: { hasError: boolean; error: Error | null };
  props: { componentId: string; children: unknown };
};

type BoundaryClass = (new (props: unknown) => BoundaryInstance) & {
  getDerivedStateFromError: (error: Error) => { hasError: true; error: Error };
};

let cached: BoundaryClass | null = null;

export function getErrorBoundary(React: ReactLike): BoundaryClass {
  if (cached) {
    return cached;
  }

  class ErrorBoundary extends React.Component {
    constructor(props: unknown) {
      super(props);
      this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): { hasError: true; error: Error } {
      return { hasError: true, error };
    }

    componentDidCatch(error: Error, info: unknown): void {
      const { componentId } = this.props as { componentId: string };
      console.error(`[SDX React] Component "${componentId}" crashed:`, error, info);
      window.dispatchEvent(
        new CustomEvent('sdx:component-error', {
          detail: {
            componentId,
            error: error.message,
            stack: error.stack ?? '',
          },
        }),
      );
    }

    render(): unknown {
      const { hasError, error } = this.state as { hasError: boolean; error: Error | null };
      if (hasError) {
        return React.createElement(
          'div',
          {
            style: {
              padding: '16px',
              border: '1px solid #dc2626',
              borderRadius: '8px',
              background: '#fef2f2',
              color: '#991b1b',
              fontSize: '14px',
            },
          },
          React.createElement('strong', null, 'Component Error'),
          React.createElement(
            'p',
            {
              style: { margin: '8px 0 0', fontFamily: 'monospace', fontSize: '12px' },
            },
            error ? error.message : 'Unknown error',
          ),
        );
      }
      return (this.props as { children: unknown }).children;
    }
  }

  cached = ErrorBoundary as unknown as BoundaryClass;
  return cached;
}
